import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { ja } from 'react-day-picker/locale';
import { Calendar } from 'lucide-react';
import 'react-day-picker/style.css';

const POPOVER_MARGIN = 8; // 画面端との最小マージン
const POPOVER_NATURAL_HEIGHT = 420; // カレンダーの自然な高さの上限（6週表示の月でも収まる余裕を持たせた値）

// 検査履歴タブのfrom/to検索欄向け。ネイティブの<input type="date">を2つ並べると
// 開始日・終了日を別々の操作で選ぶ必要があるため、1つのカレンダーで範囲選択できるようにする。
// from/toはこれまでの呼び出し側(inspDateFrom/inspDateTo等)と同じ"YYYY-MM-DD"文字列で受け渡す
export function DateRangePicker({ from, to, onChange, className = '' }) {
  const [open, setOpen] = useState(false);
  // ボタンの下に十分な空きが無い場合は上向きに開く。ポップオーバー自体には高さの上限も
  // overflow-y-autoも設けない（react-day-pickerのnav/caption/weekdaysをstickyにして
  // 内部スクロールに追従させようとすると、position:absoluteの前提が崩れて表示位置が
  // ずれる副作用が大きいため断念した）。カレンダーの自然な高さは月ごとにほぼ一定
  // （約400px）で、開く側を選ぶだけで大半のケースは画面内に収まる。それでも収まらない
  // 場合は、このコンポーネントが必ず使われているfixed inset-0＋overflow-y-autoの
  // 管理パネル側のスクロールに任せる（ヘッダー/nav/フッターが一体のまま動くため、
  // 内部スクロールとの位置ズレが原理的に発生しない）
  const [side, setSide] = useState('bottom');
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const updateSide = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - POPOVER_MARGIN;
      const spaceAbove = rect.top - POPOVER_MARGIN;
      // 上に開くのは「本当にカレンダー全体が収まる」時だけに限定する。中途半端に
      // 上向きに開くと、カレンダーの上端が画面より上（固定ヘッダーの裏側等）に出て、
      // ページスクロールでは絶対に届かない位置に固定されてしまう。下向きに開いて
      // 画面外に出る分は、管理パネル側のoverflow-y-autoなコンテナが可変長のため
      // 下スクロールで必ず届く（absolute配置の子要素はスクロール領域を広げる）
      setSide(spaceBelow < POPOVER_NATURAL_HEIGHT && spaceAbove >= POPOVER_NATURAL_HEIGHT ? 'top' : 'bottom');
    };
    updateSide();
    window.addEventListener('resize', updateSide);
    return () => window.removeEventListener('resize', updateSide);
  }, [open]);

  const selected = {
    from: from ? new Date(from + 'T00:00:00') : undefined,
    to: to ? new Date(to + 'T00:00:00') : undefined,
  };

  const toIso = (d) => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const handleSelect = (range) => {
    onChange({ from: toIso(range?.from), to: toIso(range?.to) });
  };

  const label = from && to ? `${from} 〜 ${to}` : from ? `${from} 〜` : to ? `〜 ${to}` : '期間を指定（未指定）';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-full p-2 border rounded text-sm flex items-center gap-2 text-left ${from || to ? 'text-slate-800' : 'text-slate-400'}`}
      >
        <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <span className="truncate">{label}</span>
      </button>
      {open && (
        <div className={`absolute z-20 bg-white border rounded-lg shadow-lg p-2 ${side === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
          <DayPicker
            mode="range"
            locale={ja}
            selected={selected}
            onSelect={handleSelect}
            defaultMonth={selected.from || selected.to || undefined}
            captionLayout="dropdown"
          />
          <div className="flex justify-between px-2 pb-1">
            <button
              type="button"
              onClick={() => { onChange({ from: '', to: '' }); }}
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              クリア
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs font-bold text-emerald-700 hover:text-emerald-800"
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
